import React, { useCallback } from 'react';
import { Button, Col, FlexboxGrid, Row, Text } from 'rsuite';
import {
  buildSingleLicenseTaxPeriod,
  expandQuarterlyPeriods,
} from '../../../common/taxFlowUtils';
import { License } from '../../../types/licenses';
import { setTaxSteps } from '../../../store/slices/flow/flowThunks';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import { updateTaxFlowData } from '../../../store/slices/tax/taxSlice';
import { selectConfigTaxPaymentPeriod } from '../../../store/slices/config/configSelectors';
import { useAppDispatch } from '../../../store/store';
import { useSelector } from 'react-redux';

interface UnreportedLicensePeriodProps {
  address: string | undefined;
  applicationNumber: string | undefined;
  hideTaxFlow?: boolean;
  license?: License | null;
  licenseNumber: string | undefined;
  periodEndDate: string;
  periodLabel: string;
  periodStartDate: string;
  totPayable: boolean;
}

const UnreportedLicensePeriod = ({
  address,
  applicationNumber,
  hideTaxFlow,
  license,
  licenseNumber,
  periodEndDate,
  periodLabel,
  periodStartDate,
  totPayable,
}: UnreportedLicensePeriodProps) => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useAppDispatch();
  // --- config selectors ---
  const taxPaymentPeriod = useSelector(selectConfigTaxPaymentPeriod);

  const onFlowStepsMapped = useCallback(
    (path?: string) => placeNavigate(`/${path}`),
    [placeNavigate]
  );

  const initiatePayTax = useCallback(() => {
    if (totPayable && !hideTaxFlow) {
      const period = buildSingleLicenseTaxPeriod({
        address: address ?? '',
        applicationNumber: applicationNumber ?? '',
        endDate: periodEndDate,
        label: periodLabel,
        licenseNumber: licenseNumber ?? '',
        startDate: periodStartDate,
      });

      const selectedLicenses = expandQuarterlyPeriods(
        [period],
        license?.totPaymentPeriod,
        taxPaymentPeriod
      );

      dispatch(updateTaxFlowData({ selectedLicenses }));
      dispatch(
        setTaxSteps(
          onFlowStepsMapped,
          true,
          `/single-permit/${applicationNumber}`
        )
      );
    }
  }, [
    address,
    applicationNumber,
    dispatch,
    hideTaxFlow,
    licenseNumber,
    onFlowStepsMapped,
    periodEndDate,
    periodLabel,
    periodStartDate,
    license,
    taxPaymentPeriod,
    totPayable,
  ]);

  return (
    <FlexboxGrid
      align="middle"
      className="reporting-history-item-header unreported-history-accordion-header"
      role="grid"
    >
      <FlexboxGrid.Item
        as={Row}
        colspan={24}
        style={{ alignItems: 'center' }}
      >
        <Col
          className="pb-lg-lower-8"
          md={8}
          xs={12}
        >
          <Text className="text-large text-bold">{periodLabel}</Text>
        </Col>
        {totPayable && (
          <>
            <Col
              className="pb-lg-lower-8"
              md={8}
              xs={12}
            >
              <Text
                className="sm-text-right"
                style={{ textAlign: 'center' }}
              >
                {hideTaxFlow ? 'Not required' : 'Not yet reported'}
              </Text>
            </Col>
            {!hideTaxFlow && (
              <Col
                md={8}
                style={{ textAlign: 'right' }}
                xs={24}
              >
                <Button
                  className="sm-100-percent btn-primary"
                  onClick={initiatePayTax}
                >
                  Pay Tax
                </Button>
              </Col>
            )}
          </>
        )}
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default UnreportedLicensePeriod;
